import endsWith from 'lodash/endsWith'
import includes from 'lodash/includes'
import startsWith from 'lodash/startsWith'
import toNumber from 'lodash/toNumber'
import type { PivotCondition } from '@ztechapps/common.contract'
import { PivotFieldTypeEnum, PivotFilterOperatorEnum, PivotRelativeUnitEnum } from '@ztechapps/common.contract'
import { isPivotRelativeOperator, toDate } from '@ztechapps/common.utils'

const O = PivotFilterOperatorEnum
const DAY_FORMAT = 'YYYY-MM-DD'
const RELATIVE_OFFSETS: Partial<Record<PivotFilterOperatorEnum, number>> = { [O.last]: -1, [O.current]: 0, [O.next]: 1 }

/** The whole day, Monday-started week, month, quarter or year that is `offset` periods from the one holding `today`. */
const toRelativeRange = (today: string, unit: PivotRelativeUnitEnum, offset: number): { from: string; to: string } => {
  const periodUnit = unit === PivotRelativeUnitEnum.week ? 'isoWeek' : unit
  const start = toDate(today).add(offset, unit).startOf(periodUnit)
  return { from: start.format(DAY_FORMAT), to: start.clone().endOf(periodUnit).format(DAY_FORMAT) }
}

const compare = (left: number | string, right: number | string): number => (left < right ? -1 : left > right ? 1 : 0)

/**
 * Whether a member passes a Condition. `memberKey` is the member key of the field (`''` is blank, an ISO day for an
 * ungrouped date), `today` the viewer's local `YYYY-MM-DD` day. Numbers compare by value, strings ignoring case,
 * days as their ISO text; a blank member never passes, whatever the operator.
 */
export const applyConditionFilter = ({
  condition,
  memberKey,
  fieldType,
  today,
}: {
  condition: PivotCondition
  memberKey: string
  fieldType: PivotFieldTypeEnum
  today: string
}): boolean => {
  if (memberKey === '') return false
  const { operator, values, unit } = condition
  if (isPivotRelativeOperator(operator)) {
    if (unit === undefined) return false
    const { from, to } = toRelativeRange(today, unit, RELATIVE_OFFSETS[operator] ?? 0)
    return from <= memberKey && memberKey <= to
  }
  const read = (text: string): number | string => {
    if (fieldType === PivotFieldTypeEnum.Number) return toNumber(text)
    return fieldType === PivotFieldTypeEnum.String ? text.toLowerCase() : text
  }
  const member = read(memberKey)
  const [first, second] = values.map(read)
  if (first === undefined || Number.isNaN(member) || Number.isNaN(first)) return false
  const isText = typeof member === 'string' && typeof first === 'string'
  const order = compare(member, first)
  switch (operator) {
    case O.equal:
      return order === 0
    case O.not_equal:
      return order !== 0
    case O.greater:
    case O.after:
      return order > 0
    case O.greater_equal:
    case O.after_equal:
      return order >= 0
    case O.less:
    case O.before:
      return order < 0
    case O.less_equal:
    case O.before_equal:
      return order <= 0
    case O.begin:
      return isText && startsWith(member, first)
    case O.not_begin:
      return isText && !startsWith(member, first)
    case O.end:
      return isText && endsWith(member, first)
    case O.not_end:
      return isText && !endsWith(member, first)
    case O.contain:
      return isText && includes(member, first)
    case O.not_contain:
      return isText && !includes(member, first)
    case O.between:
    case O.not_between: {
      if (second === undefined || Number.isNaN(second)) return false
      const isInside = order >= 0 && compare(member, second) <= 0
      return operator === O.between ? isInside : !isInside
    }
    default:
      return false
  }
}
